import { useCallback, useEffect, useRef } from 'react';

import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { getConfluenceEditorClientId } from '@atlassian/rovo-content-bridge-api-commands/clients/confluence-smart-create-editor-client';
import { ROVO_CHAT_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/rovo-chat-client';
import { ReadContentCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/read-content-command';
import { getBrowserBridgeAPI } from '@atlassian/rovo-content-bridge-api/singleton';

import type { InsertedContentIdentity, ScheduleRemixRetentionCheck } from './types';

const ACTIONED_EVENT_DELAY = 30000;

const adfContainsIdentity = (value: unknown, identity: InsertedContentIdentity): boolean => {
	if (Array.isArray(value)) {
		return value.some((item) => adfContainsIdentity(item, identity));
	}
	if (typeof value !== 'object' || value === null) {
		return false;
	}

	const node = value as { attrs?: Record<string, unknown>; content?: unknown };
	if (node.attrs?.[identity.attribute] === identity.value) {
		return true;
	}

	return adfContainsIdentity(node.content, identity);
};

export const useRemixRetentionTracker = (): ScheduleRemixRetentionCheck => {
	const { trackAIMAUEvent } = useAnalytics();
	const trackAIMAUEventRef = useRef(trackAIMAUEvent);
	trackAIMAUEventRef.current = trackAIMAUEvent;
	const isActiveRef = useRef(true);
	const pendingKeysRef = useRef(new Set<string>());
	const completedKeysRef = useRef(new Set<string>());
	const timeoutIdsRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());

	useEffect(() => {
		const timeoutIds = timeoutIdsRef.current;
		const pendingKeys = pendingKeysRef.current;
		const completedKeys = completedKeysRef.current;
		isActiveRef.current = true;

		return () => {
			isActiveRef.current = false;
			timeoutIds.forEach((timeoutId) => clearTimeout(timeoutId));
			timeoutIds.clear();
			pendingKeys.clear();
			completedKeys.clear();
		};
	}, []);

	return useCallback<ScheduleRemixRetentionCheck>(
		({ analyticsEvent, identity, insertedAt = Date.now(), interactionSource, receiverId }) => {
			const trackingKey = `${receiverId}:${identity.attribute}:${identity.value}`;
			if (
				!isActiveRef.current ||
				pendingKeysRef.current.has(trackingKey) ||
				completedKeysRef.current.has(trackingKey)
			) {
				return;
			}

			pendingKeysRef.current.add(trackingKey);
			const timeoutId = setTimeout(
				() => {
					timeoutIdsRef.current.delete(trackingKey);
					try {
						void getBrowserBridgeAPI()
							.send(ReadContentCommand, {
								senderId: ROVO_CHAT_CLIENT_ID,
								receiverId: getConfluenceEditorClientId(receiverId),
								...(interactionSource ? { source: interactionSource } : {}),
							})
							.result()
							.then((content) => {
								if (
									isActiveRef.current &&
									pendingKeysRef.current.has(trackingKey) &&
									content?.contentType === 'page' &&
									adfContainsIdentity(content.pageBody, identity)
								) {
									completedKeysRef.current.add(trackingKey);
									trackAIMAUEventRef.current(analyticsEvent);
								}
							})
							.catch(() => undefined)
							.finally(() => pendingKeysRef.current.delete(trackingKey));
					} catch {
						pendingKeysRef.current.delete(trackingKey);
					}
				},
				Math.max(0, ACTIONED_EVENT_DELAY - (Date.now() - insertedAt)),
			);
			timeoutIdsRef.current.set(trackingKey, timeoutId);
		},
		[],
	);
};
